import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';

import { useGetProducts, useDeleteProduct, useDeleteProducts } from 'api';

import CustomNoRowsOverlay from 'components/noRowsOverlay/customNoRowsOverlay';
import DataGridHeader from 'components/dataGridHeader';
import DataGridCell from 'components/dataGridCell';
import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { Box, Button, Loader } from 'components/ui';
import { ProductFormModal } from 'components/containers';

import * as Styled from './Inventory.styles';
import DataGridActionsMenu from 'components/dataGridActionsMenu';
import { DeleteListItemConfirmationModal } from 'components/DeleteListItemConfirmationModal/DeleteListItemConfirmationModal';
import { getLabelForProductType } from 'utility/productTypes';

export const Inventory = () => {
  const [editProductData, setEditProductData] = useState(null);

  const [deleteProductGuids, setDeleteProductGuids] = React.useState([]);
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const [deleteDialogConfirmationOpen, setDeleteDialogConfirmationOpen] = React.useState(false);

  const {
    data: productsData,
    refetch: refetchProductsData,
    isLoading: isProductsDataLoading,
  } = useGetProducts({
    offset: 0,
    limit: 999
  });

  const { mutate: deleteProduct, isLoading: isProductDeleting } = useDeleteProduct(
    deleteProductGuids[0],
    {
      onSuccess: () => {
        refetchProductsData();
        setDeleteProductGuids([]);
        setDeleteDialogConfirmationOpen(false);
      },
    },
    {},
  );

  const { mutate: deleteProducts, isLoading: isProductsDeleting } = useDeleteProducts(
    deleteProductGuids,
    {
      onSuccess: () => {
        refetchProductsData();
        setDeleteProductGuids([]);
        setDeleteDialogConfirmationOpen(false);
      },
    },
    {},
  );

  const renderColHeader = (params) => <DataGridHeader>{params.colDef.headerName}</DataGridHeader>;

  const handleEditClick = (rowId) => {
    const data = productsData.filter((product) => product.id === rowId)[0];
    setEditProductData(data);
  };

  const handleDeleteProductClick = (rowId) => {
    setDeleteDialogConfirmationOpen(true);
    setDeleteProductGuids([rowId]);      
  }

  const handleMenuOptionClick = (optionId, rowId) => {
    switch (optionId) {
      case 'delete':
        handleDeleteProductClick(rowId);
        break;
      case 'edit':
        handleEditClick(rowId);
        break;
      default:
        break;
    }
  };

  const handleDeleteProductAgreed = () => {
    if (deleteProductGuids.length === 1) {
      deleteProduct();
    } else if (deleteProductGuids.length > 1) {
      deleteProducts();
    }
  };

  const handleDeleteProductDisagreed = () => {
    setDeleteProductGuids([]);
    setDeleteDialogConfirmationOpen(false);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nazwa',
      width: 250,
      filterable: false,
      sortable: false,
      type: 'string',
      renderCell: (params) => <DataGridCell main rowId={params.row.id} itemClicked={handleEditClick}>{params.row.product.name}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'pkWiU',
      headerName: 'PKWiU',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      type: 'string',
      renderCell: (params) => <DataGridCell>{params.row.product.pkWiU}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'unit',
      headerName: 'Jednostka',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      type: 'string',
      renderCell: (params) => <DataGridCell>{params.row.product.unit}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'unitNetPrice',
      headerName: 'Cena netto',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      type: 'string',
      renderCell: (params) => <DataGridCell>{params.row.product.unitNetPrice}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'type',
      headerName: 'Rodzaj',
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      type: 'string',
      renderCell: (params) => <DataGridCell>{getLabelForProductType(params.row.product.type)}</DataGridCell>,
      renderHeader: renderColHeader,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Akcje',
      width: 120,
      cellClassName: 'actions',
      headerAlign: 'right',
      align: 'right',
      renderHeader: renderColHeader,
      renderCell: (params) => (
        <DataGridActionsMenu
          remove
          rowId={params.row.id}
          handleMenuOptionClick={handleMenuOptionClick}
        />
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {deleteDialogConfirmationOpen && (
        <DeleteListItemConfirmationModal
          id="delete-confirmation-dialog"
          onConfirm={handleDeleteProductAgreed}
          onClose={handleDeleteProductDisagreed}
          isSaving={isProductsDeleting }
          title={selectedProducts?.length > 1 ? 'Usunąć produkty?' : 'Usunąć produkt?'}
          confirmText={selectedProducts?.length > 1 ? 'Tak, usuń produkty' : 'Tak, usuń produkt'}
          message={
            selectedProducts?.length > 1
              ? 'Czy na pewno chcesz usunąć produkty? Tej operacji nie będzie można cofnąć.'
              : 'Czy na pewno chcesz usunąć produkt? Tej operacji nie będzie można cofnąć.'
          }
        />
      )}
      <Styled.Wrapper>
        {isProductsDataLoading ? (
          <Loader />
        ) : (
          <>
            <Button className="addProductButton" onClick={() => setEditProductData({})}>
              Dodaj produkt
            </Button>

            <Box title="Produkty">
              <DataGrid
                sx={{ '& > .MuiDataGrid-columnSeparator': { visibility: 'hidden' }, border: 'none' }}
                autoHeight
                rows={productsData || []}
                rowHeight={64}
                getRowHeight={() => 64}
                columns={columns}
                disableColumnFilter
                disableColumnMenu
                hideFooter
                components={{
                  NoRowsOverlay: CustomNoRowsOverlay,
                }}
              />
            </Box>
          </>
        )}

        {!!editProductData && (
          <ProductFormModal
            onClose={() => setEditProductData(null)}
            productData={editProductData}
            onSuccess={() => {
              setEditProductData(null);
              refetchProductsData();
            }}
          />
        )}
      </Styled.Wrapper>
    </DashboardLayout>
  );
};
